import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Our Work", to: "/our-work" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "border-b border-charcoal/10 bg-ivory/85 backdrop-blur-md" : "bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="wrap flex h-[72px] items-center justify-between">
          <Link to="/" aria-label="Heritage Constructions — home" data-testid="nav-home-logo" className="shrink-0">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `relative font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-gold after:transition-all after:duration-300 ${
                    isActive ? "text-charcoal after:w-full" : "text-charcoal/55 after:w-0 hover:text-charcoal hover:after:w-full"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-5 lg:flex">
            <a href="tel:+61456492849" data-testid="nav-phone-link" className="flex items-center gap-2 text-sm font-medium text-charcoal/70 transition-colors hover:text-charcoal">
              <Phone size={15} strokeWidth={1.75} />
              0456 492 849
            </a>
            <Link to="/contact" data-testid="nav-request-quote-button" className="btn-dark !px-6 !py-2.5">
              Request a Quote
            </Link>
          </div>

          <button
            className="flex h-11 w-11 items-center justify-center rounded-full border border-charcoal/15 text-charcoal lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={19} /> : <Menu size={19} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col bg-ivory pt-28 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <nav className="wrap flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    className={({ isActive }) =>
                      `flex items-center justify-between border-b border-charcoal/10 py-4 font-serif text-3xl ${
                        isActive ? "text-gold" : "text-charcoal"
                      }`
                    }
                  >
                    {l.label}
                    <ArrowUpRight size={20} className="text-charcoal/30" />
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.4 }}
              className="wrap mt-auto mb-10 flex flex-col gap-3"
            >
              <a href="tel:+61456492849" className="btn-ghost" data-testid="nav-mobile-phone">
                <Phone size={16} /> Call 0456 492 849
              </a>
              <Link to="/contact" className="btn-dark" data-testid="nav-mobile-quote">
                Request a Quote
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
